import { isAxiosError } from 'axios';
import { pushApi } from '../api/endpoints';
import { registerStaffWorker, withDeadline } from './serviceWorker';

export type PushState =
  | 'loading'
  | 'insecure'
  | 'unsupported'
  | 'unavailable'
  | 'worker_offline'
  | 'available'
  | 'denied'
  | 'active'
  | 'error';

let cachedPublicKey = '';
let cachedRegistration: ServiceWorkerRegistration | null = null;
let workerRunning = false;
let pendingState: Promise<PushState> | null = null;

export const closeServiceRequestNotifications = async (ids: number[]): Promise<void> => {
  if (!ids.length || !window.isSecureContext || !('serviceWorker' in navigator)) return;
  try {
    const registration = await withDeadline(navigator.serviceWorker.getRegistration(), 'Notificações indisponíveis.', 5000);
    const notifications = await registration?.getNotifications();
    for (const notification of notifications ?? []) {
      if (ids.includes(notification.data?.serviceRequestId)) notification.close();
    }
  } catch { /* Dismissing an OS notification must not block attendance. */ }
};

const applicationServerKey = (value: string): Uint8Array<ArrayBuffer> => {
  const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`;
  const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
};

const supported = () =>
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const serviceWorker = async () => {
  if (cachedRegistration) return cachedRegistration;
  cachedRegistration = await registerStaffWorker();
  return cachedRegistration;
};

const save = async (subscription: PushSubscription) => {
  await pushApi.registar(
    subscription.toJSON(),
    `${navigator.platform || 'Mobile'} · ${navigator.userAgent.slice(0, 80)}`,
  );
};

const restoreSubscription = async (registration: ServiceWorkerRegistration, renew = false): Promise<PushSubscription> => {
  const key = applicationServerKey(cachedPublicKey);
  let subscription = await withDeadline(registration.pushManager.getSubscription(), 'Não foi possível consultar as notificações neste dispositivo.');
  const existingKey = subscription?.options.applicationServerKey;
  const differentKey = existingKey && (existingKey.byteLength !== key.byteLength
    || !new Uint8Array(existingKey).every((byte, index) => byte === key[index]));
  if (subscription && (renew || differentKey)) {
    const previousEndpoint = subscription.endpoint;
    const removed = await withDeadline(subscription.unsubscribe(), 'Não foi possível renovar as notificações.');
    if (!removed) throw new Error('Não foi possível renovar as notificações. Tenta novamente.');
    await withDeadline(pushApi.remover(previousEndpoint), 'Não foi possível remover a subscrição anterior.', 3000).catch(() => undefined);
    subscription = null;
  }
  // Once permission has been granted, recover a subscription removed by a
  // logout, browser cleanup or key rotation without asking on every login.
  return subscription ?? withDeadline(registration.pushManager.subscribe({
    userVisibleOnly: true, applicationServerKey: key,
  }), 'Não foi possível ativar as notificações neste dispositivo.');
};

const saveOrRenew = async (registration: ServiceWorkerRegistration, subscription: PushSubscription): Promise<void> => {
  try { await save(subscription); }
  catch (error) {
    if (!isAxiosError<{ code?: string }>(error) || error.response?.status !== 409
      || error.response.data?.code !== 'push_subscription_inactive') throw error;
    // O servidor já invalidou este endpoint. Renovar uma vez, sem voltar a
    // pedir permissão e sem declarar sucesso se o novo registo também falhar.
    await save(await restoreSubscription(registration, true));
  }
};

const checkPushState = async (renew = false): Promise<PushState> => {
  if (!window.isSecureContext) return 'insecure';
  if (!supported()) return 'unsupported';
  const registration = await serviceWorker();
  const config = await pushApi.configuracao();
  if (!config.enabled || !config.public_key) return 'unavailable';
  cachedPublicKey = config.public_key;
  workerRunning = config.worker_running;
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'available';

  const subscription = await restoreSubscription(registration, renew);
  await saveOrRenew(registration, subscription);
  return workerRunning ? 'active' : 'worker_offline';
};

const runStateCheck = (renew = false): Promise<PushState> => {
  // Arranque, regresso à app e recuperação da rede podem coincidir. Uma
  // subscrição só pode ser substituída por uma dessas verificações de cada vez.
  if (pendingState && !renew) return pendingState;
  const previous = pendingState;
  const next = (previous ? previous.catch(() => undefined) : Promise.resolve())
    .then(() => checkPushState(renew));
  pendingState = next;
  const clear = () => { if (pendingState === next) pendingState = null; };
  void next.then(clear, clear);
  return next;
};

export const currentPushState = (): Promise<PushState> => runStateCheck();

/** A pedido do utilizador: substitui uma subscrição local que deixou de receber. */
export const renewPushNotifications = (): Promise<PushState> => runStateCheck(true);

export const testPushNotifications = async (): Promise<void> => {
  const state = await currentPushState();
  if (state !== 'active' && state !== 'worker_offline') {
    throw new Error('Ativa as notificações neste dispositivo antes de testar.');
  }
  const registration = await serviceWorker();
  const subscription = await withDeadline(registration.pushManager.getSubscription(), 'Não foi possível consultar as notificações.');
  if (!subscription) throw new Error('Ativa as notificações neste dispositivo antes de testar.');
  await pushApi.testar(subscription.endpoint);
};

export const enablePushNotifications = async (): Promise<PushState> => {
  if (!window.isSecureContext) return 'insecure';
  if (!supported()) return 'unsupported';
  if (!cachedPublicKey || !cachedRegistration) return 'unavailable';

  // No iOS, este pedido tem de acontecer diretamente no gesto do utilizador.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'available';

  const registration = cachedRegistration;
  const subscription = await restoreSubscription(registration);
  await saveOrRenew(registration, subscription);
  return workerRunning ? 'active' : 'worker_offline';
};

export const disablePushNotifications = async (): Promise<void> => {
  if (!window.isSecureContext || !supported()) return;
  const registration = await withDeadline(navigator.serviceWorker.getRegistration('/'), 'Não foi possível desligar as notificações.', 3000);
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await withDeadline(pushApi.remover(subscription.endpoint), 'Não foi possível desligar as notificações.', 3000).catch(() => undefined);
  await subscription.unsubscribe();
};
